/**
 * Les 6 Dofus (GDD §11). Module pur.
 *
 * Principe : **chaque Dofus teste un système différent du jeu**. Quand le
 * joueur a fini d'optimiser un axe, le suivant le pousse dans un axe qu'il
 * avait négligé. C'est ce qui tient un joueur sur la durée.
 *
 * Ce sont des équipements, portés dans 6 emplacements dédiés, et leurs effets
 * valent pour **tout le compte**, pas pour un personnage.
 */
import type { Etat } from "./etat.ts"
import type { Contenu } from "./contenu.ts"
import { item } from "./contenu.ts"
import { perfection, panoplies } from "./objets.ts"
import { niveauMetier } from "./recolte.ts"

export interface EffetDofus {
  degats?: number
  recolte?: number
  vitesse?: number
  regen?: number
  prospection?: number
  doubleJet?: boolean
}

export interface Progres {
  fait: boolean
  actuel: number
  requis: number
}

export interface Dofus {
  id: number
  nom: string
  /** identifiant de l'objet Dofus dans le jeu, pour afficher sa vraie icône */
  item: number
  couleur: string
  /** le système que ce Dofus met à l'épreuve */
  systeme: string
  condition: string
  effet: EffetDofus
  progres: (e: Etat, c: Contenu) => Progres
}

/**
 * Un craft qui vaut d'être tenté sa chance.
 *
 * La condition du Turquoise demandait « un craft à 100 % », n'importe lequel :
 * 980 recettes n'ont aucun effet variable et sortent donc parfaites par
 * construction, et 1 305 autres donnent plus d'une chance sur vingt. Le Dofus
 * tombait au premier pain fabriqué.
 *
 * On exige donc un vrai pari : niveau 50 au moins et trois effets variables, ce
 * qui met la probabilité entre un millième et un cinq-millième. 1 490 recettes
 * y répondent, réparties sur toute la progression.
 */
const NIVEAU_PARI = 50
const EFFETS_PARI = 3
const craftDePari = (c: Contenu, id: number): boolean => {
  const it = item(c, id)
  if (!it || it.niveau < NIVEAU_PARI) return false
  return it.effets.filter((e) => e.max > e.min).length >= EFFETS_PARI
}

/**
 * Cette recette compte-t-elle pour le Turquoise ?
 *
 * Exporté pour que l'atelier puisse le dire sur la recette elle-même. Spiritus a
 * cru le Dofus cassé après un craft parfait : « j'avais un effet qui était pas
 * variable, le PM ». La condition l'écrivait pourtant — « trois effets variables
 * au moins » — mais rien, sur une recette, ne disait **lesquels** de ses effets
 * le sont. Une règle qu'on ne peut pas vérifier sur l'objet qu'on tient est une
 * règle qu'on croit fausse.
 */
export const compteTurquoise = (c: Contenu, id: number): boolean => craftDePari(c, id)

/** Combien d'effets d'un objet se tirent au hasard. */
export const effetsVariables = (c: Contenu, id: number): number =>
  item(c, id)?.effets.filter((e) => e.max > e.min).length ?? 0

/** Les seuils du pari, pour les afficher sans les redire en dur. */
export { NIVEAU_PARI, EFFETS_PARI }

const p = (actuel: number, requis: number): Progres =>
  ({ fait: actuel >= requis, actuel: Math.min(actuel, requis), requis })

/**
 * Les seuils sont vérifiés atteignables sur le contenu embarqué : 100 des 306
 * archimonstres, 3 des 418 panoplies, un métier à 100 sur 200, 4 personnages sur
 * 8 emplacements. Ils restent volontairement en deçà du maximum : un Dofus doit
 * demander de la constance, pas l'épuisement du jeu.
 */
export const DOFUS: Dofus[] = [
  {
    id: 1, nom: "Dofus Émeraude", item: 737, couleur: "#5aa469",
    systeme: "l'endurance en récolte", condition: "monter un métier au niveau 100",
    effet: { recolte: 1.5 },
    progres: (e) => p(Math.max(0, ...e.personnages.flatMap((x) =>
      Object.keys(x.metiers).map((m) => niveauMetier(x, Number(m))))), 100),
  },
  {
    id: 2, nom: "Dofus Pourpre", item: 694, couleur: "#9b4a8f",
    systeme: "la puissance brute",
    condition: "vaincre le boss d'un donjon du plus haut niveau du jeu",
    effet: { degats: 1.5 },
    /**
     * **N'importe lequel des donjons du plus haut palier, pas un en
     * particulier.**
     *
     * La condition disait « le donjon le plus élevé » et le code triait par
     * niveau pour prendre le premier — or **trente-quatre donjons partagent le
     * niveau 190**, et le tri en désignait un au hasard des données, le
     * Transporteur de Sylargh. Spiritus a acheté sept millions de kamas la clé
     * de l'Autel de la Déchireuse, l'a vaincu, et n'a rien eu : son donjon était
     * du bon niveau mais pas celui que le tri avait élu.
     *
     * Une épreuve dont l'énoncé désigne trente-quatre portes et le code une
     * seule n'est pas difficile, elle est déloyale. Le palier entier compte.
     */
    progres: (e, c) => {
      const donjons = Object.values(c.donjons)
      const plusHaut = donjons.reduce((a, d) => Math.max(a, d.niveauMin), 0)
      const boss = new Set(donjons.filter((d) => d.niveauMin === plusHaut).flatMap((d) => d.boss))
      const vaincu = e.bossVaincus.some((b) => boss.has(b))
      return p(vaincu ? 1 : 0, 1)
    },
  },
  {
    id: 3, nom: "Dofus Turquoise", item: 739, couleur: "#3f9aa8",
    systeme: "la chance et le craft",
    condition: "sortir un craft parfait sur un objet de niveau 50 ou plus, à trois effets variables au moins",
    effet: { doubleJet: true },
    progres: (e, c) => p(Object.entries(e.recordsCraft)
      .filter(([id, v]) => v === 100 && craftDePari(c, Number(id))).length, 1),
  },
  {
    id: 4, nom: "Dofus Ocre", item: 7754, couleur: "#c9793a",
    systeme: "la présence", condition: "capturer 100 archimonstres",
    effet: { vitesse: 1.5 },
    progres: (e) => p(e.archimonstres.length, 100),
  },
  {
    id: 5, nom: "Dofus Ivoire", item: 7115, couleur: "#ded4bd",
    systeme: "la collection", condition: "compléter 3 panoplies entières",
    effet: { prospection: 100 },
    progres: (e, c) => {
      let completes = 0
      for (const x of e.personnages) {
        for (const [setId, n] of panoplies(x.equipement, c)) {
          const pano = c.panoplies[String(setId)]
          if (pano && n >= pano.items.length) completes++
        }
      }
      return p(completes, 3)
    },
  },
  {
    id: 6, nom: "Dofus Cawotte", item: 972, couleur: "#e0a53f",
    systeme: "l'équipe", condition: "4 personnages de classes différentes au niveau 50",
    effet: { regen: 1.5 },
    progres: (e) => {
      const classes = new Set(e.personnages.filter((x) => x.niveau >= 50).map((x) => x.classe))
      return p(classes.size, 4)
    },
  },
]

export const DOFUS_EBENE: Dofus = {
  id: 7, nom: "Dofus Ébène", item: 7114, couleur: "#2b2b30",
  systeme: "tout", condition: "posséder les six autres Dofus",
  effet: { degats: 1.25, recolte: 1.25, vitesse: 1.25, regen: 1.25, prospection: 50 },
  progres: (e) => p(e.dofus.filter((d) => d <= 6).length, 6),
}

export const TOUS_DOFUS = [...DOFUS, DOFUS_EBENE]

/** Cumul des effets des Dofus obtenus, à l'échelle du compte. */
export function effetsDofus(e: Etat): Required<Omit<EffetDofus, "doubleJet">> & { doubleJet: boolean } {
  const t = { degats: 1, recolte: 1, vitesse: 1, regen: 1, prospection: 0, doubleJet: false }
  for (const d of TOUS_DOFUS) {
    if (!e.dofus.includes(d.id)) continue
    t.degats *= d.effet.degats ?? 1
    t.recolte *= d.effet.recolte ?? 1
    t.vitesse *= d.effet.vitesse ?? 1
    t.regen *= d.effet.regen ?? 1
    t.prospection += d.effet.prospection ?? 0
    t.doubleJet ||= Boolean(d.effet.doubleJet)
  }
  return t
}

/** Les Dofus nouvellement mérités, à ajouter à la collection. */
export function dofusMerites(e: Etat, c: Contenu): number[] {
  return TOUS_DOFUS
    .filter((d) => !e.dofus.includes(d.id) && d.progres(e, c).fait)
    .map((d) => d.id)
}

export { perfection }
